import { useState, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Wallet, ArrowsClockwise, ArrowDown } from '@phosphor-icons/react';
import api from '../lib/api';

const fmt = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const fmtDate = (d) => d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '-';

export default function BalanceTransferPage() {
  const { user } = useAuth();
  const role = user?.role;
  const isStaff = role === 'staff';
  const isSubStaff = role === 'sub_staff';

  const [team, setTeam] = useState({ self: {}, sub_staff: [] });
  const [wallet, setWallet] = useState({ amazon_balance: 0, card_balance: 0 });
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ to_sub_staff_id: '', source: 'amazon', amount: '', note: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      if (isStaff) {
        const [t, h] = await Promise.all([
          api.get('/balance-transfer/team-summary'),
          api.get('/balance-transfer/list'),
        ]);
        setTeam(t.data);
        setHistory(h.data || []);
      } else if (isSubStaff) {
        const w = await api.get('/balance-transfer/my-wallet');
        setWallet({ amazon_balance: w.data.amazon_balance, card_balance: w.data.card_balance });
        setHistory(w.data.history || []);
      } else {
        const h = await api.get('/balance-transfer/list');
        setHistory(h.data || []);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Load failed');
    } finally { setLoading(false); }
  }, [isStaff, isSubStaff]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    const amt = Number(form.amount);
    if (!form.to_sub_staff_id) { toast.error('Sub-staff select karo'); return; }
    if (!amt || amt <= 0) { toast.error('Valid amount daalo'); return; }
    setBusy(true);
    try {
      const { data } = await api.post('/balance-transfer', {
        to_sub_staff_id: form.to_sub_staff_id,
        source: form.source,
        amount: amt,
        note: form.note,
      });
      toast.success(data.message);
      setShowNew(false);
      setForm({ to_sub_staff_id: '', source: 'amazon', amount: '', note: '' });
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Transfer failed'); }
    finally { setBusy(false); }
  };

  const reverse = async (tid) => {
    if (!window.confirm('Reverse karna hai? Sub-staff ke balance se wapas aapke pas chala jayega.')) return;
    try {
      const { data } = await api.post(`/balance-transfer/${tid}/reverse`);
      toast.success(data.message);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Reverse failed'); }
  };

  return (
    <div className="space-y-4" data-testid="balance-transfer-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Wallet size={26} className="text-indigo-600" /> Balance Transfer</h1>
          <p className="text-sm text-gray-500">
            {isStaff && 'Apne sub-staff ko Amazon ya Card balance transfer karo. Reverse anytime.'}
            {isSubStaff && 'Aapka wallet — Amazon + Card balance + transfer history.'}
            {!isStaff && !isSubStaff && 'Saari intra-team transfers ka audit trail.'}
          </p>
        </div>
        {isStaff && (
          <Button onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700 gap-1" data-testid="new-transfer-btn">
            <ArrowDown size={16} /> New Transfer
          </Button>
        )}
      </div>

      {/* Staff: team balance summary */}
      {isStaff && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="bg-white border rounded p-4">
            <div className="text-[10px] uppercase text-gray-500 font-bold">My Balance ({team.self?.name})</div>
            <div className="flex gap-4 mt-2">
              <div>
                <div className="text-[11px] text-gray-500">Amazon</div>
                <div className="text-xl font-bold text-orange-600">{fmt(team.self?.amazon_balance)}</div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500">Card</div>
                <div className="text-xl font-bold text-purple-600">{fmt(team.self?.card_balance)}</div>
              </div>
            </div>
          </div>
          <div className="bg-white border rounded p-4">
            <div className="text-[10px] uppercase text-gray-500 font-bold">Team Total</div>
            <div className="flex gap-4 mt-2">
              <div>
                <div className="text-[11px] text-gray-500">Sub-staff Amazon</div>
                <div className="text-xl font-bold text-orange-600">
                  {fmt((team.sub_staff || []).reduce((a, s) => a + (s.amazon_balance || 0), 0))}
                </div>
              </div>
              <div>
                <div className="text-[11px] text-gray-500">Sub-staff Card</div>
                <div className="text-xl font-bold text-purple-600">
                  {fmt((team.sub_staff || []).reduce((a, s) => a + (s.card_balance || 0), 0))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sub-staff: own wallet */}
      {isSubStaff && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-orange-50 border border-orange-200 rounded p-4">
            <div className="text-[10px] uppercase text-orange-700 font-bold">Amazon Balance</div>
            <div className="text-3xl font-bold text-orange-700 mt-1">{fmt(wallet.amazon_balance)}</div>
          </div>
          <div className="bg-purple-50 border border-purple-200 rounded p-4">
            <div className="text-[10px] uppercase text-purple-700 font-bold">Card Balance</div>
            <div className="text-3xl font-bold text-purple-700 mt-1">{fmt(wallet.card_balance)}</div>
          </div>
        </div>
      )}

      {/* Staff: per sub-staff balance table */}
      {isStaff && (team.sub_staff || []).length > 0 && (
        <div className="overflow-x-auto bg-white border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Sub-Staff</th>
                <th className="px-3 py-2 text-right">Amazon</th>
                <th className="px-3 py-2 text-right">Card</th>
                <th className="px-3 py-2 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {(team.sub_staff || []).map(s => (
                <tr key={s.id} className="border-t hover:bg-gray-50">
                  <td className="px-3 py-2 font-medium">{s.name} <span className="text-[10px] text-gray-500">{s.email}</span></td>
                  <td className="px-3 py-2 text-right text-orange-700 font-semibold">{fmt(s.amazon_balance)}</td>
                  <td className="px-3 py-2 text-right text-purple-700 font-semibold">{fmt(s.card_balance)}</td>
                  <td className="px-3 py-2 text-right font-bold">{fmt((s.amazon_balance || 0) + (s.card_balance || 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Transfer history */}
      <div className="overflow-x-auto bg-white border rounded">
        <div className="px-3 py-2 bg-gray-50 border-b text-sm font-semibold flex items-center gap-2">
          <ArrowsClockwise size={16} /> Transfer History ({history.length})
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-[11px] uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2 text-left">When</th>
              <th className="px-3 py-2 text-left">From</th>
              <th className="px-3 py-2 text-left">To</th>
              <th className="px-3 py-2 text-left">Source</th>
              <th className="px-3 py-2 text-right">Amount</th>
              <th className="px-3 py-2 text-left">Direction</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="text-center text-gray-400 py-6">Loading…</td></tr>
            ) : history.length === 0 ? (
              <tr><td colSpan={8} className="text-center text-gray-400 py-6">Koi transfer abhi tak nahi.</td></tr>
            ) : history.map(t => (
              <tr key={t.id} className="border-t hover:bg-gray-50" data-testid={`transfer-${t.id}`}>
                <td className="px-3 py-2 text-[11px] text-gray-500">{fmtDate(t.created_at)}</td>
                <td className="px-3 py-2">{t.from_staff_name}</td>
                <td className="px-3 py-2">{t.to_sub_staff_name}</td>
                <td className="px-3 py-2 capitalize">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.source === 'amazon' ? 'bg-orange-50 text-orange-700 border-orange-300' : 'bg-purple-50 text-purple-700 border-purple-300'}`}>
                    {t.source}
                  </span>
                </td>
                <td className="px-3 py-2 text-right font-semibold">{fmt(t.amount)}</td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.direction === 'reverse' ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-blue-50 text-blue-700 border-blue-300'}`}>
                    {t.direction === 'reverse' ? '↩ Reverse' : '→ Forward'}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full border ${t.status === 'reversed' ? 'bg-gray-100 text-gray-600 border-gray-300' : 'bg-emerald-50 text-emerald-700 border-emerald-300'}`}>
                    {t.status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  {isStaff && t.direction === 'forward' && t.status === 'completed' && t.from_staff_id === user?.id && (
                    <Button size="sm" variant="outline" className="h-7 text-xs gap-1 border-amber-300 text-amber-700 hover:bg-amber-50" onClick={() => reverse(t.id)} data-testid={`reverse-${t.id}`}>
                      <ArrowsClockwise size={12} /> Reverse
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* New Transfer Dialog */}
      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Balance Transfer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold">Sub-Staff *</label>
              <select value={form.to_sub_staff_id} onChange={e => setForm({ ...form, to_sub_staff_id: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded text-sm" data-testid="transfer-substaff-select">
                <option value="">— Select —</option>
                {(team.sub_staff || []).map(s => (
                  <option key={s.id} value={s.id}>{s.name} (Amazon: {fmt(s.amazon_balance)}, Card: {fmt(s.card_balance)})</option>
                ))}
              </select>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-xs font-semibold">Source *</label>
                <select value={form.source} onChange={e => setForm({ ...form, source: e.target.value })} className="w-full mt-1 px-3 py-2 border rounded text-sm" data-testid="transfer-source-select">
                  <option value="amazon">Amazon (Available: {fmt(team.self?.amazon_balance)})</option>
                  <option value="card">Card (Available: {fmt(team.self?.card_balance)})</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-semibold">Amount (₹) *</label>
                <Input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="0" data-testid="transfer-amount-input" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold">Note (optional)</label>
              <Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} className="mt-1" placeholder="e.g., October allocation" />
            </div>
            <div className="text-[11px] text-blue-700 bg-blue-50 p-2 rounded border border-blue-200">
              ℹ️ Aap ka <b>{form.source === 'amazon' ? 'Amazon' : 'Card'}</b> balance debit hoga, sub-staff ka credit hoga. Reverse anytime.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)} disabled={busy}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="confirm-transfer-btn">
              {busy ? 'Transferring…' : 'Transfer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
